import {
  deleteProjectsByProjectIdFeatures,
  getProjectsByProjectIdFeatures,
  patchProjectsByProjectIdFeatures,
  postProjectsByProjectIdFeatures,
  type Feature,
  type FeatureCollection,
} from '../generated';
import type { Client as FeaturesClient } from '../generated/client/types.gen';

import {
  childGeospatialLogger,
  type GeospatialLogger,
  GeospatialFeature,
  FeatureCoordinateType,
  FeatureGeometry,
  FeatureScope,
  FeatureServiceSupport,
  IFeatureService,
  logPrettyResponse,
  Position3D,
} from '@trimble/geospatial';
import { FeaturesApiServiceError } from './features-api-service';

const LAYER_ID_METADATA_KEY = 'common_layerId';

/** IFeatureService backed by Trimble Features Service (writes + targeted reads). */
export class FeaturesServiceFeatureStore implements IFeatureService {
  public readonly support: FeatureServiceSupport = {
    supportsPerFeatureProperties: true,
  };

  private readonly logger: GeospatialLogger;

  constructor(
    private readonly client: FeaturesClient,
    logger: GeospatialLogger
  ) {
    this.logger = childGeospatialLogger(logger, 'FeaturesServiceFeatureStore');
  }

  public async loadLayerFeatures(
    scope: FeatureScope,
    layerId: string,
    coordinateType: FeatureCoordinateType = 'original'
  ): Promise<GeospatialFeature[]> {
    const filter = JSON.stringify([
      {
        field: `metadata.${LAYER_ID_METADATA_KEY}`,
        operator: 'EQ',
        operand: layerId,
      },
    ]);
    this.logger.debug('loadLayerFeatures request', { layerId, coordinateType });
    const res = await getProjectsByProjectIdFeatures({
      client: this.client,
      path: { projectId: scope.projectId },
      query: { filter, coordinateType },
      throwOnError: true,
    });
    logPrettyResponse(this.logger, 'loadLayerFeatures', res.data);
    const features = res.data?.features ?? [];
    this.logger.info('loadLayerFeatures response', {
      layerId,
      count: features.length,
    });
    return features
      .map((feature) => this.fromApiFeature(feature, layerId))
      .filter((feature): feature is GeospatialFeature => feature !== undefined);
  }

  public async createFeature(
    scope: FeatureScope,
    feature: GeospatialFeature
  ): Promise<GeospatialFeature> {
    this.logger.debug('createFeature request', {
      layerId: feature.layerId,
      crsUrn: feature.crsUrn,
    });
    const res = await postProjectsByProjectIdFeatures({
      client: this.client,
      path: { projectId: scope.projectId },
      query: { echo: true },
      body: this.buildCollection(feature),
      throwOnError: true,
    });
    logPrettyResponse(this.logger, 'createFeature', res.data);
    const created = this.requireSavedFeature(
      res.data,
      res.request,
      res.response,
      'createFeature'
    );
    this.logger.info('createFeature response', { id: created.id });
    return (
      this.fromApiFeature(created, feature.layerId) ?? {
        ...feature,
        id: created.id,
      }
    );
  }

  public async createFeatures(
    scope: FeatureScope,
    features: readonly GeospatialFeature[]
  ): Promise<GeospatialFeature[]> {
    if (features.length === 0) {
      return [];
    }
    this.logger.debug('createFeatures request', { count: features.length });
    const res = await postProjectsByProjectIdFeatures({
      client: this.client,
      path: { projectId: scope.projectId },
      query: { echo: true },
      body: { features: features.map((f) => this.buildWritableFeature(f)) },
      throwOnError: true,
    });
    logPrettyResponse(this.logger, 'createFeatures', res.data);
    const created = res.data?.features ?? [];
    this.logger.info('createFeatures response', { count: created.length });
    return created
      .map((feature) => this.fromApiFeature(feature, ''))
      .filter((feature): feature is GeospatialFeature => feature !== undefined);
  }

  public async updateFeature(
    scope: FeatureScope,
    feature: GeospatialFeature
  ): Promise<GeospatialFeature> {
    this.logger.debug('updateFeature request', {
      id: feature.id,
      layerId: feature.layerId,
    });
    const res = await patchProjectsByProjectIdFeatures({
      client: this.client,
      path: { projectId: scope.projectId },
      query: { echo: true },
      body: this.buildCollection(feature),
      throwOnError: true,
    });
    logPrettyResponse(this.logger, 'updateFeature', res.data);
    const updated = this.requireSavedFeature(
      res.data,
      res.request,
      res.response,
      'updateFeature'
    );
    this.logger.info('updateFeature response', { id: updated.id });
    return this.fromApiFeature(updated, feature.layerId) ?? feature;
  }

  public async updateFeatureProperties(
    scope: FeatureScope,
    featureId: string,
    layerId: string,
    values: Readonly<Record<string, unknown>>
  ): Promise<void> {
    this.logger.debug('updateFeatureProperties request', {
      id: featureId,
      keys: Object.keys(values),
    });
    const body: FeatureCollection = {
      features: [
        {
          id: featureId,
          properties: { ...values },
          metadata: { [LAYER_ID_METADATA_KEY]: layerId },
        },
      ],
    };
    const res = await patchProjectsByProjectIdFeatures({
      client: this.client,
      path: { projectId: scope.projectId },
      body,
      throwOnError: true,
    });
    logPrettyResponse(this.logger, 'updateFeatureProperties', res.data);
    this.logger.info('updateFeatureProperties response', { id: featureId });
  }

  public async deleteFeatures(
    scope: FeatureScope,
    featureIds: readonly string[]
  ): Promise<void> {
    if (featureIds.length === 0) {
      return;
    }
    this.logger.debug('deleteFeatures request', { count: featureIds.length });
    const res = await deleteProjectsByProjectIdFeatures({
      client: this.client,
      path: { projectId: scope.projectId },
      body: { features: featureIds.map((id) => ({ id })) },
      throwOnError: true,
    });
    logPrettyResponse(this.logger, 'deleteFeatures', res.data);
    this.logger.info('deleteFeatures response', { count: featureIds.length });
  }

  /**
   * Writes send `echo: true`, so the response holds the saved feature. A
   * response without one means the service dropped the write; returning the
   * request feature would mark it as saved.
   */
  private requireSavedFeature(
    data: FeatureCollection | undefined | void,
    request: Request,
    response: Response,
    operation: 'createFeature' | 'updateFeature'
  ): Feature & { readonly id: string } {
    const saved = data ? data.features?.[0] : undefined;
    const id = saved?.id;
    if (!saved || !id) {
      throw new FeaturesApiServiceError(
        `Features Service ${operation} response has no saved feature`,
        { request, response }
      );
    }
    return { ...saved, id };
  }

  private buildCollection(feature: GeospatialFeature): FeatureCollection {
    return { features: [this.buildWritableFeature(feature)] };
  }

  private buildWritableFeature(feature: GeospatialFeature): Feature {
    return {
      id: feature.id,
      geometry: {
        type: feature.geometry.type,
        // Generated client types Point coords as number[][]; Polygon rings
        // are number[][][]. Widen via unknown so Point/LineString stay valid.
        coordinates: feature.geometry.coordinates as unknown as number[][],
      },
      properties: { ...feature.properties },
      metadata: {
        ...feature.metadata,
        [LAYER_ID_METADATA_KEY]: feature.layerId,
      },
      crs: this.toNamedCrs(feature.crsUrn),
    };
  }

  private toNamedCrs(crsUrn: string | undefined): Feature['crs'] {
    if (!crsUrn) {
      return undefined;
    }
    return { properties: { name: crsUrn } };
  }

  private fromApiFeature(
    feature: Feature,
    fallbackLayerId: string
  ): GeospatialFeature | undefined {
    const geometry = this.fromApiGeometry(feature.geometry);
    if (!geometry) {
      return undefined;
    }
    const metadata = feature.metadata ?? {};
    const layerId =
      typeof metadata[LAYER_ID_METADATA_KEY] === 'string'
        ? (metadata[LAYER_ID_METADATA_KEY] as string)
        : fallbackLayerId;
    return {
      id: feature.id ?? undefined,
      layerId,
      geometry,
      properties: feature.properties ?? {},
      metadata,
      crsUrn: feature.crs?.properties?.name ?? undefined,
    };
  }

  private fromApiGeometry(
    geometry: Feature['geometry']
  ): FeatureGeometry | undefined {
    const coordinates = geometry?.coordinates as unknown;
    if (!geometry || !Array.isArray(coordinates)) {
      return undefined;
    }
    if (geometry.type === 'Point' && typeof coordinates[0] === 'number') {
      return { type: 'Point', coordinates: coordinates as Position3D };
    }
    if (geometry.type === 'LineString' && Array.isArray(coordinates[0])) {
      return { type: 'LineString', coordinates: coordinates as Position3D[] };
    }
    if (
      geometry.type === 'Polygon' &&
      Array.isArray(coordinates[0]) &&
      Array.isArray((coordinates[0] as unknown[])[0])
    ) {
      return { type: 'Polygon', coordinates: coordinates as Position3D[][] };
    }
    return undefined;
  }
}
